import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useLocalized } from "@/hooks/useLocalized";
import { Card, CardContent } from "@/components/ui/card";
import { Star, Quote, ChevronLeft, ChevronRight } from "lucide-react";

const TestimonialsCarousel = ({ testimonials, loading, sectionRef }) => {
  const { t } = useTranslation();
  const { l } = useLocalized();
  const [activeTestimonial, setActiveTestimonial] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  useEffect(() => {
    if (testimonials.length <= 1 || isPaused) return;
    const interval = setInterval(() => {
      setActiveTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [testimonials.length, isPaused]);

  const nextTestimonial = () => setActiveTestimonial((prev) => (prev + 1) % testimonials.length);
  const prevTestimonial = () => setActiveTestimonial((prev) => (prev - 1 + testimonials.length) % testimonials.length);

  return (
    <section
      data-testid="testimonials-section"
      className="py-20 md:py-32 bg-[#3D405B]"
      ref={sectionRef}
    >
      <div className="max-w-7xl mx-auto px-6">
        <div className="section-header reveal">
          <span className="text-[#F2CC8F] font-medium tracking-wider uppercase text-sm">
            {t("home.testimonials.badge")}
          </span>
          <h2 className="mt-3 text-white">{t("home.testimonials.title")}</h2>
          <p className="text-[#F4F1DE]/70">{t("home.testimonials.subtitle")}</p>
        </div>

        {(() => {
          if (loading) {
            return (
              <div className="flex justify-center">
                <div className="spinner"></div>
              </div>
            );
          }
          if (testimonials.length === 0) return null;
          return (
          <div
            className="reveal relative max-w-3xl mx-auto"
            onMouseEnter={() => setIsPaused(true)}
            onMouseLeave={() => setIsPaused(false)}
            data-testid="testimonial-carousel"
          >
            <div className="relative overflow-hidden min-h-[280px]">
              {testimonials.map((testimonial, idx) => {
                let slideClass = "opacity-0 translate-x-full";
                if (idx === activeTestimonial) {
                  slideClass = "opacity-100 translate-x-0";
                } else if (idx < activeTestimonial) {
                  slideClass = "opacity-0 -translate-x-full";
                }
                return (
                  <div
                    key={testimonial.id}
                    data-testid={`testimonial-card-${idx}`}
                    className={`absolute inset-0 transition-all duration-700 ease-in-out ${slideClass}`}
                  >
                    <Card className="bg-white/5 border-white/10 backdrop-blur testimonial-glow h-full">
                      <CardContent className="p-8 md:p-12 text-center">
                        <Quote className="w-12 h-12 text-[#F2CC8F] mx-auto mb-6" />
                        <p className="text-[#F4F1DE] italic leading-relaxed text-lg md:text-xl mb-8">
                          &ldquo;{l(testimonial, "comment")}&rdquo;
                        </p>
                        <div className="flex items-center justify-center gap-4">
                          <div className="w-14 h-14 bg-[#E07A5F] rounded-full flex items-center justify-center text-white font-semibold text-xl">
                            {testimonial.customer_name.charAt(0)}
                          </div>
                          <div className="text-left">
                            <p className="text-white font-semibold text-lg">{testimonial.customer_name}</p>
                            <p className="text-[#F4F1DE]/60 text-sm">{l(testimonial, "event_type")}</p>
                          </div>
                        </div>
                        <div className="flex gap-1 mt-4 justify-center">
                          {[...Array(5)].map((_, i) => (
                            <Star
                              key={`star-${testimonial.id}-${i}`}
                              className={`w-5 h-5 ${
                                i < testimonial.rating
                                  ? "text-[#F2CC8F] fill-current"
                                  : "text-[#F4F1DE]/30"
                              }`}
                            />
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                );
              })}
            </div>

            {testimonials.length > 1 && (
              <>
                <button
                  onClick={prevTestimonial}
                  data-testid="testimonial-prev-btn"
                  className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 md:-translate-x-14 w-10 h-10 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center text-white transition-colors"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <button
                  onClick={nextTestimonial}
                  data-testid="testimonial-next-btn"
                  className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 md:translate-x-14 w-10 h-10 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center text-white transition-colors"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </>
            )}

            {testimonials.length > 1 && (
              <div className="flex justify-center gap-2 mt-8" data-testid="testimonial-dots">
                {testimonials.map((testimonial, idx) => (
                  <button
                    key={testimonial.id}
                    onClick={() => setActiveTestimonial(idx)}
                    data-testid={`testimonial-dot-${idx}`}
                    className={`w-3 h-3 rounded-full transition-all duration-300 ${
                      idx === activeTestimonial
                        ? "bg-[#F2CC8F] w-8"
                        : "bg-white/30 hover:bg-white/50"
                    }`}
                  />
                ))}
              </div>
            )}
          </div>
          );
        })()}
      </div>
    </section>
  );
};

export default TestimonialsCarousel;
